import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  DocumentSnapshot,
  Timestamp,
  increment,
  arrayUnion,
  arrayRemove,
  documentId
} from 'firebase/firestore'
import { db } from '../config/firebase'

// Interfaces
export interface Badge {
  id: string
  name: string
  description: string
  icon: string
  color: string
  category: 'study' | 'exam' | 'streak' | 'achievement' | 'social'
  rarity: 'common' | 'rare' | 'epic' | 'legendary'
  unlockedAt?: string
  progress?: number
  maxProgress?: number
  conditions: {
    type: 'study_time' | 'exam_count' | 'streak_days' | 'perfect_score' | 'login_days' |
          'exams_completed' | 'current_streak' | 'longest_streak' | 'level' | 'xp' | 'friends_count' |
          'notes_created' | 'summaries_created' | 'pomodoros_completed' | 'perfect_exams' | 'consecutive_days'
    target: number
    description: string
  }[]
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface UserBadge {
  id: string
  userId: string
  badgeId: string
  unlockedAt: string
  progress?: number
  maxProgress?: number
}

export interface Achievement {
  id: string
  name: string
  description: string
  icon: string
  category: string
  points: number
  requirements: {
    type: string
    target: number
    description: string
  }[]
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface UserProgress {
  id: string
  userId: string
  level: number
  xp: number
  coins: number
  totalStudyTime: number
  totalExamsCompleted: number
  currentStreak: number
  longestStreak: number
  badges: string[]
  achievements: string[]
  lastActivityDate: string
  createdAt: string
  updatedAt: string
}

export interface BadgeProgress {
  id: string
  userId: string
  badgeId: string
  currentProgress: number
  maxProgress: number
  isCompleted: boolean
  completedAt?: string
  createdAt: string
  updatedAt: string
}

export interface LeaderboardEntry {
  id: string
  userId: string
  username: string
  avatar?: string
  level: number
  xp: number
  rank: number
  weeklyXp: number
  monthlyXp: number
}

class GamificationService {
  // Collections
  private badgesCollection = collection(db, 'badges')
  private userBadgesCollection = collection(db, 'userBadges')
  private achievementsCollection = collection(db, 'achievements')
  private userProgressCollection = collection(db, 'userProgress')
  private badgeProgressCollection = collection(db, 'badgeProgress')
  private leaderboardCollection = collection(db, 'leaderboard')

  // Badge Methods
  async getAllBadges(): Promise<Badge[]> {
    try {
      const q = query(this.badgesCollection, where('isActive', '==', true), orderBy('category'), orderBy('rarity'))
      const querySnapshot = await getDocs(q)
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Badge[]
    } catch (error) {
      console.error('Error fetching badges:', error)
      throw error
    }
  }

  async getBadgeById(badgeId: string): Promise<Badge | null> {
    try {
      const docRef = doc(this.badgesCollection, badgeId)
      const docSnap = await getDoc(docRef)
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as Badge
      }
      return null
    } catch (error) {
      console.error('Error fetching badge:', error)
      throw error
    }
  }

  async getUserBadges(userId: string): Promise<UserBadge[]> {
    try {
      const q = query(
        this.userBadgesCollection, 
        where('userId', '==', userId),
        orderBy('unlockedAt', 'desc')
      )
      const querySnapshot = await getDocs(q)
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as UserBadge[]
    } catch (error) {
      console.error('Error fetching user badges:', error)
      throw error
    }
  }

  async getUnlockedBadges(userId: string): Promise<Badge[]> {
    try {
      const userBadges = await this.getUserBadges(userId)
      const badgeIds = userBadges.map(ub => ub.badgeId)
      
      if (badgeIds.length === 0) return []
      
      const badges: Badge[] = []
      // Firestore'da 'in' operatörü maksimum 10 element alabilir
      for (let i = 0; i < badgeIds.length; i += 10) {
        const chunk = badgeIds.slice(i, i + 10)
        const q = query(this.badgesCollection, where(documentId(), 'in', chunk))
        const querySnapshot = await getDocs(q)
        
        querySnapshot.docs.forEach(doc => {
          const badge = { id: doc.id, ...doc.data() } as Badge
          const userBadge = userBadges.find(ub => ub.badgeId === doc.id)
          if (userBadge) {
            badge.unlockedAt = userBadge.unlockedAt
          }
          badges.push(badge)
        })
      }
      
      return badges.sort((a, b) => new Date(b.unlockedAt!).getTime() - new Date(a.unlockedAt!).getTime())
    } catch (error) {
      console.error('Error fetching unlocked badges:', error)
      throw error
    }
  }

  async getAvailableBadges(userId: string): Promise<Badge[]> {
    try {
      const [allBadges, userBadges] = await Promise.all([
        this.getAllBadges(),
        this.getUserBadges(userId)
      ])
      
      const unlockedBadgeIds = new Set(userBadges.map(ub => ub.badgeId))
      return allBadges.filter(badge => !unlockedBadgeIds.has(badge.id))
    } catch (error) {
      console.error('Error fetching available badges:', error)
      throw error
    }
  }

  async unlockBadge(userId: string, badgeId: string): Promise<UserBadge> {
    try {
      const userBadgeData = {
        userId,
        badgeId,
        unlockedAt: new Date().toISOString()
      }
      
      const docRef = await addDoc(this.userBadgesCollection, userBadgeData)
      
      // Update user progress
      await this.updateUserProgress(userId, {
        badges: arrayUnion(badgeId)
      })
      
      return {
        id: docRef.id,
        ...userBadgeData
      }
    } catch (error) {
      console.error('Error unlocking badge:', error)
      throw error
    }
  }

  // Badge Progress Methods
  async getBadgeProgress(userId: string): Promise<BadgeProgress[]> {
    try {
      const q = query(this.badgeProgressCollection, where('userId', '==', userId), orderBy('updatedAt', 'desc'))
      const querySnapshot = await getDocs(q)
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as BadgeProgress[]
    } catch (error) {
      console.error('Error fetching badge progress:', error)
      throw error
    }
  }

  async updateBadgeProgress(
    userId: string, 
    badgeId: string, 
    currentProgress: number, 
    maxProgress: number
  ): Promise<void> {
    try {
      const q = query(
        this.badgeProgressCollection,
        where('userId', '==', userId),
        where('badgeId', '==', badgeId)
      )
      const querySnapshot = await getDocs(q)
      
      const isCompleted = currentProgress >= maxProgress
      const progressData = {
        userId,
        badgeId,
        currentProgress,
        maxProgress,
        isCompleted,
        updatedAt: new Date().toISOString(),
        ...(isCompleted && { completedAt: new Date().toISOString() })
      }
      
      if (querySnapshot.empty) {
        // Create new progress record
        await addDoc(this.badgeProgressCollection, {
          ...progressData,
          createdAt: new Date().toISOString()
        })
      } else {
        // Update existing progress
        const docRef = querySnapshot.docs[0].ref
        await updateDoc(docRef, progressData)
      }
      
      // If badge is completed, unlock it
      if (isCompleted) {
        const existingUserBadge = await this.getUserBadges(userId)
        const hasUserBadge = existingUserBadge.some(ub => ub.badgeId === badgeId)
        
        if (!hasUserBadge) {
          await this.unlockBadge(userId, badgeId)
        }
      }
    } catch (error) {
      console.error('Error updating badge progress:', error)
      throw error
    }
  }

  // User Progress Methods
  async getUserProgress(userId: string): Promise<UserProgress | null> {
    try {
      const q = query(this.userProgressCollection, where('userId', '==', userId))
      const querySnapshot = await getDocs(q)
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0]
        return {
          id: doc.id,
          ...doc.data()
        } as UserProgress
      }
      return null
    } catch (error) {
      console.error('Error fetching user progress:', error)
      throw error
    }
  }

  async createUserProgress(userId: string): Promise<UserProgress> {
    try {
      const progressData = {
        userId,
        level: 1,
        xp: 0,
        coins: 0,
        totalStudyTime: 0,
        totalExamsCompleted: 0,
        currentStreak: 0,
        longestStreak: 0,
        badges: [],
        achievements: [],
        lastActivityDate: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
      
      const docRef = await addDoc(this.userProgressCollection, progressData)
      
      return {
        id: docRef.id,
        ...progressData
      }
    } catch (error) {
      console.error('Error creating user progress:', error)
      throw error
    }
  }

  async updateUserProgress(userId: string, updates: { [K in keyof UserProgress]?: UserProgress[K] | import('firebase/firestore').FieldValue }): Promise<void> {
    try {
      const q = query(this.userProgressCollection, where('userId', '==', userId))
      const querySnapshot = await getDocs(q)
      
      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref
        await updateDoc(docRef, {
          ...updates,
          updatedAt: new Date().toISOString()
        })
      } else {
        // Create new progress if doesn't exist
        await this.createUserProgress(userId)
      }
    } catch (error) {
      console.error('Error updating user progress:', error)
      throw error
    }
  }

  // Achievement Methods
  async getAllAchievements(): Promise<Achievement[]> {
    try {
      const q = query(this.achievementsCollection, where('isActive', '==', true), orderBy('category'))
      const querySnapshot = await getDocs(q)
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Achievement[]
    } catch (error) {
      console.error('Error fetching achievements:', error)
      throw error
    }
  }

  // Leaderboard Methods
  async getLeaderboard(limitCount: number = 50): Promise<LeaderboardEntry[]> {
    try {
      const q = query(
        this.leaderboardCollection,
        orderBy('xp', 'desc'),
        limit(limitCount)
      )
      const querySnapshot = await getDocs(q)
      
      return querySnapshot.docs.map((doc, index) => ({
        id: doc.id,
        rank: index + 1,
        ...doc.data()
      })) as LeaderboardEntry[]
    } catch (error) {
      console.error('Error fetching leaderboard:', error)
      throw error
    }
  }

  async updateLeaderboard(userId: string, userData: Partial<LeaderboardEntry>): Promise<void> {
    try {
      const q = query(this.leaderboardCollection, where('userId', '==', userId))
      const querySnapshot = await getDocs(q)
      
      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref
        await updateDoc(docRef, userData)
      } else {
        await addDoc(this.leaderboardCollection, {
          userId,
          ...userData
        })
      }
    } catch (error) {
      console.error('Error updating leaderboard:', error)
      throw error
    }
  }

  // Badge Eligibility Check
  async checkBadgeEligibility(userId: string): Promise<string[]> {
    try {
      const [userProgress, availableBadges, badgeProgress] = await Promise.all([
        this.getUserProgress(userId),
        this.getAvailableBadges(userId),
        this.getBadgeProgress(userId)
      ])
      
      if (!userProgress) return []
      
      const newlyUnlockedBadges: string[] = []
      
      for (const badge of availableBadges) {
        let canUnlock = true
        let totalProgress = 0
        let totalMaxProgress = 0
        
        for (const condition of badge.conditions) {
          let currentValue = 0
          
          switch (condition.type) {
            case 'study_time':
              currentValue = userProgress.totalStudyTime
              break
            case 'exams_completed':
              currentValue = userProgress.totalExamsCompleted
              break
            case 'current_streak':
              currentValue = userProgress.currentStreak
              break
            case 'longest_streak':
              currentValue = userProgress.longestStreak
              break
            case 'level':
              currentValue = userProgress.level
              break
            case 'xp':
              currentValue = userProgress.xp
              break
            default:
              currentValue = 0
          }
          
          totalProgress += Math.min(currentValue, condition.target)
          totalMaxProgress += condition.target
          
          if (currentValue < condition.target) {
            canUnlock = false
          }
        }
        
        // Update badge progress
        await this.updateBadgeProgress(userId, badge.id, totalProgress, totalMaxProgress)
        
        if (canUnlock) {
          newlyUnlockedBadges.push(badge.id)
        }
      }
      
      return newlyUnlockedBadges
    } catch (error) {
      console.error('Error checking badge eligibility:', error)
      throw error
    }
  }

  // XP and Level Management
  async addXP(userId: string, xpAmount: number): Promise<void> {
    try {
      const userProgress = await this.getUserProgress(userId)
      if (!userProgress) {
        await this.createUserProgress(userId)
        return this.addXP(userId, xpAmount)
      }
      
      const newXP = userProgress.xp + xpAmount
      const newLevel = Math.floor(newXP / 1000) + 1 // Her 1000 XP'de bir level
      
      await this.updateUserProgress(userId, {
        xp: newXP,
        level: newLevel
      })
      
      // Update leaderboard
      await this.updateLeaderboard(userId, {
        xp: newXP,
        level: newLevel
      })
      
      // Check for new badge eligibility
      await this.checkBadgeEligibility(userId)
    } catch (error) {
      console.error('Error adding XP:', error)
      throw error
    }
  }

  // Activity Tracking
  async trackStudySession(userId: string, duration: number): Promise<void> {
    try {
      const userProgress = await this.getUserProgress(userId)
      if (!userProgress) {
        await this.createUserProgress(userId)
        return this.trackStudySession(userId, duration)
      }
      
      const today = new Date().toDateString()
      const lastActivityDate = new Date(userProgress.lastActivityDate).toDateString()
      
      let newStreak = userProgress.currentStreak
      if (today !== lastActivityDate) {
        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)
        
        if (lastActivityDate === yesterday.toDateString()) {
          newStreak += 1
        } else {
          newStreak = 1
        }
      }
      
      await this.updateUserProgress(userId, {
        totalStudyTime: userProgress.totalStudyTime + duration,
        currentStreak: newStreak,
        longestStreak: Math.max(userProgress.longestStreak, newStreak),
        lastActivityDate: new Date().toISOString()
      })
      
      // Add XP for study session
      const xpGained = Math.floor(duration / 60) * 10 // 10 XP per minute
      await this.addXP(userId, xpGained)
    } catch (error) {
      console.error('Error tracking study session:', error)
      throw error
    }
  }

  async trackExamCompletion(userId: string, score: number): Promise<void> {
    try {
      const userProgress = await this.getUserProgress(userId)
      if (!userProgress) {
        await this.createUserProgress(userId)
        return this.trackExamCompletion(userId, score)
      }
      
      await this.updateUserProgress(userId, {
        totalExamsCompleted: userProgress.totalExamsCompleted + 1
      })
      
      // Add XP based on score
      const xpGained = Math.floor(score * 10) // 10 XP per point
      await this.addXP(userId, xpGained)
    } catch (error) {
      console.error('Error tracking exam completion:', error)
      throw error
    }
  }
}

// Mock data for development
const studyBadgeNames = [
  'İlk Adım', 'Çalışkan Öğrenci', 'Bilgi Avcısı', 'Kitap Kurdu', 'Odaklanmış Zihin', 'Sabırlı Öğrenci', 'Bilgi Toplayıcı', 'Çalışma Makinesi', 'Zihin Jimnastiği', 'Konsantrasyon Ustası',
  'Bilgi Bankası', 'Çalışma Ritmi', 'Zihin Gücü', 'Öğrenme Tutkusu', 'Bilgi Madencisi', 'Çalışma Disiplini', 'Zihin Atletı', 'Bilgi Mimarı', 'Çalışma Sanatçısı', 'Zihin Savaşçısı',
  'Bilgi Efendisi', 'Çalışma Efsanesi', 'Zihin Dehası', 'Bilgi Kralı', 'Çalışma Tanrısı', 'Zihin Büyücüsü', 'Bilgi Filozofu', 'Çalışma Şampiyonu', 'Zihin Ustası', 'Bilgi Kahramanı',
  'Çalışma Devi', 'Zihin Titani', 'Bilgi İmparatoru', 'Çalışma Efsanesi', 'Zihin Fenomeni', 'Bilgi Destanı', 'Çalışma Mucizesi', 'Zihin Harikası', 'Bilgi Evrensel', 'Çalışma Sonsuzluğu',
  'Zihin Galaksisi', 'Bilgi Kozmik', 'Çalışma Evreni', 'Zihin Boyutu', 'Bilgi Sınırsızlığı', 'Çalışma Mutlakiyeti', 'Zihin Mükemmelliği', 'Bilgi Kusursuzluğu', 'Çalışma Nirvana', 'Zihin Aydınlanması'
]

const examBadgeNames = [
  'Sınav Başlangıcı', 'Test Savaşçısı', 'Soru Avcısı', 'Cevap Ustası', 'Sınav Kahramanı', 'Test Efendisi', 'Soru Çözücü', 'Cevap Makinesi', 'Sınav Devi', 'Test Titani',
  'Soru Dehası', 'Cevap Fenomeni', 'Sınav Efsanesi', 'Test Mucizesi', 'Soru Harikası', 'Cevap Büyücüsü', 'Sınav İmparatoru', 'Test Kralı', 'Soru Tanrısı', 'Cevap Şampiyonu',
  'Sınav Ustası', 'Test Filozofu', 'Soru Mimarı', 'Cevap Sanatçısı', 'Sınav Destanı', 'Test Evrensel', 'Soru Kozmik', 'Cevap Galaksisi', 'Sınav Boyutu', 'Test Sınırsızlığı',
  'Soru Mutlakiyeti', 'Cevap Mükemmelliği', 'Sınav Kusursuzluğu', 'Test Nirvana', 'Soru Aydınlanması', 'Cevap Sonsuzluğu', 'Sınav Evreni', 'Test Boyutu', 'Soru Galaksisi', 'Cevap Kozmik'
]

const streakBadgeNames = [
  'Günlük Ritüel', 'Kararlı Adım', 'Süreklilik', 'Azim Gücü', 'Disiplin Ustası', 'Kararlılık', 'Sebat Gücü', 'İstikrar', 'Devamlılık', 'Sıkı Çalışma',
  'Vazgeçmezlik', 'Kararlı Yürüyüş', 'Sürekli Hareket', 'Azimli Ruh', 'Disiplinli Yaşam', 'Kararlı Kalp', 'Sebatlı Zihin', 'İstikrarlı Adım', 'Devamlı Güç', 'Sıkı Disiplin',
  'Vazgeçmez Ruh', 'Kararlı Efsane', 'Sürekli Mucize', 'Azimli Harika', 'Disiplinli Destan', 'Kararlı Evrensel', 'Sebatlı Kozmik', 'İstikrarlı Galaksi', 'Devamlı Boyut', 'Sıkı Sınırsızlık'
]

const achievementBadgeNames = [
  'Bilgi Avcısı', 'Zeka Ustası', 'Akıl Oyunları Şampiyonu', 'Strateji Dehası', 'Analitik Düşünür', 'Problem Çözücü', 'Yaratıcı Zeka', 'Mantık Savaşçısı', 'Beyin Fırtınası', 'Zeka Olimpiyatçısı',
  'Düşünce Mimarı', 'Akıl Labirenti Ustası', 'Bilgelik Arayıcısı', 'Zeka Piramidi', 'Akıl Hazinesi', 'Düşünce Sanatçısı', 'Zeka Kristali', 'Akıl Elması', 'Bilgelik Tacı', 'Zeka Efsanesi',
  'Bilgi İmparatoru', 'Akıl Filozofu', 'Zeka Büyücüsü', 'Düşünce Dehası', 'Bilgelik Ustası', 'Akıl Şampiyonu', 'Zeka Fenomeni', 'Düşünce Mucizesi', 'Bilgelik Harikası', 'Akıl Destanı',
  'Zeka Evrensel', 'Düşünce Kozmik', 'Bilgelik Galaksisi', 'Akıl Boyutu', 'Zeka Sınırsızlığı', 'Düşünce Mutlakiyeti', 'Bilgelik Mükemmelliği', 'Akıl Kusursuzluğu', 'Zeka Nirvana', 'Düşünce Aydınlanması'
]

const socialBadgeNames = [
  'İlk Arkadaş', 'Sosyal Başlangıç', 'Arkadaş Canlısı', 'Sosyal Kelebek', 'Popüler Öğrenci', 'Sosyal Lider', 'Arkadaş Mıknatısı', 'Sosyal Yıldız', 'Popülerlik Kralı', 'Sosyal İkon',
  'Arkadaşlık Ustası', 'Sosyal Ağ', 'Popülerlik Fenomeni', 'Sosyal Harika', 'Arkadaşlık Mucizesi', 'Sosyal Destan', 'Popülerlik Efsanesi', 'Sosyal Evrensel', 'Arkadaşlık Kozmik', 'Sosyal Galaksi',
  'Popülerlik Boyutu', 'Sosyal Sınırsızlık', 'Arkadaşlık Mutlakiyeti', 'Sosyal Mükemmellik', 'Popülerlik Kusursuzluğu', 'Sosyal Nirvana', 'Arkadaşlık Aydınlanması', 'Sosyal Sonsuzluk', 'Popülerlik Evreni', 'Sosyal Boyut',
  'Arkadaşlık Galaksisi', 'Sosyal Kozmos', 'Popülerlik Mutlaklığı', 'Sosyal Mükemmelliği', 'Arkadaşlık Kusursuzluğu', 'Sosyal Nirvana Evreni', 'Popülerlik Aydınlanması', 'Sosyal Sonsuzluk Boyutu', 'Arkadaşlık Galaktik', 'Sosyal Kozmik Evren'
]

const mockBadges: Badge[] = [
  // Study Badges (50 badges)
  ...Array.from({ length: 50 }, (_, i) => ({
    id: `study-${i + 1}`,
    name: studyBadgeNames[i],
    description: `${(i + 1) * 30} dakika çalışma tamamladınız`,
    icon: ['📚', '📖', '✏️', '📝', '🎯', '💡', '🧠', '📊', '📋', '🔍'][i % 10],
    color: ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336'][i % 5],
    category: 'study' as const,
    rarity: (i < 20 ? 'common' : i < 35 ? 'rare' : i < 45 ? 'epic' : 'legendary') as Badge['rarity'],
    conditions: [{ type: 'study_time' as const, target: (i + 1) * 30, description: `${(i + 1) * 30} dakika çalış` }],
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  })),

  // Exam Badges (40 badges)
  ...Array.from({ length: 40 }, (_, i) => ({
    id: `exam-${i + 1}`,
    name: examBadgeNames[i],
    description: `${i + 1} sınav tamamladınız`,
    icon: ['🏆', '🥇', '🎖️', '🏅', '👑', '💎', '⭐', '🌟', '✨', '🎊'][i % 10],
    color: ['#FFD700', '#FF6B35', '#9C27B0', '#E91E63', '#3F51B5'][i % 5],
    category: 'exam' as const,
    rarity: (i < 15 ? 'common' : i < 25 ? 'rare' : i < 35 ? 'epic' : 'legendary') as Badge['rarity'],
    conditions: [{ type: 'exams_completed' as const, target: i + 1, description: `${i + 1} sınav tamamla` }],
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  })),

  // Streak Badges (30 badges)
  ...Array.from({ length: 30 }, (_, i) => ({
    id: `streak-${i + 1}`,
    name: streakBadgeNames[i],
    description: `${i + 1} gün üst üste çalıştınız`,
    icon: ['🔥', '⚡', '💥', '🌪️', '🌊', '🌈', '☄️', '🎆', '🎇', '✨'][i % 10],
    color: ['#F44336', '#FF5722', '#FF9800', '#FFC107', '#FFEB3B'][i % 5],
    category: 'streak' as const,
    rarity: (i < 10 ? 'common' : i < 20 ? 'rare' : i < 25 ? 'epic' : 'legendary') as Badge['rarity'],
    conditions: [{ type: 'current_streak' as const, target: i + 1, description: `${i + 1} günlük seri yap` }],
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  })),

  // Achievement Badges (40 badges)
  ...Array.from({ length: 40 }, (_, i) => ({
    id: `achievement-${i + 1}`,
    name: achievementBadgeNames[i],
    description: `${i + 1}. seviyeye ulaştınız`,
    icon: ['⭐', '🌟', '✨', '💫', '🎖️', '🏅', '👑', '💎', '🔮', '🎯'][i % 10],
    color: ['#FFD700', '#FFA000', '#FF8F00', '#FF6F00', '#E65100'][i % 5],
    category: 'achievement' as const,
    rarity: (i < 15 ? 'common' : i < 25 ? 'rare' : i < 35 ? 'epic' : 'legendary') as Badge['rarity'],
    conditions: [{ type: 'level' as const, target: i + 1, description: `${i + 1}. seviyeye ulaş` }],
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  })),

  // Social Badges (40 badges)
  ...Array.from({ length: 40 }, (_, i) => ({
    id: `social-${i + 1}`,
    name: socialBadgeNames[i],
    description: `${i + 1} arkadaşla çalıştınız`,
    icon: ['👥', '🤝', '💬', '🎉', '🎊', '🎈', '🎁', '💝', '🌺', '🌸'][i % 10],
    color: ['#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3'][i % 5],
    category: 'social' as const,
    rarity: (i < 15 ? 'common' : i < 25 ? 'rare' : i < 35 ? 'epic' : 'legendary') as Badge['rarity'],
    conditions: [{ type: 'friends_count' as const, target: i + 1, description: `${i + 1} arkadaş edin` }],
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }))
]

const mockUserProgress: UserProgress = {
  id: 'progress-1',
  userId: 'mock-user',
  level: 3,
  xp: 1250,
  coins: 150,
  totalStudyTime: 300,
  totalExamsCompleted: 2,
  currentStreak: 5,
  longestStreak: 8,
  badges: ['first-study', 'study-warrior'],
  achievements: [],
  lastActivityDate: new Date().toISOString(),
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
}

const mockUserBadges: UserBadge[] = [
  {
    id: 'user-badge-1',
    userId: 'mock-user',
    badgeId: 'first-study',
    unlockedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
  },
  {
    id: 'user-badge-2',
    userId: 'mock-user',
    badgeId: 'study-warrior',
    unlockedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
  }
]

// Mock service for development
class MockGamificationService {
  async getAllBadges(): Promise<Badge[]> {
    return Promise.resolve([...mockBadges])
  }

  async getBadgeById(badgeId: string): Promise<Badge | null> {
    const badge = mockBadges.find(b => b.id === badgeId)
    return Promise.resolve(badge || null)
  }

  async getUserBadges(userId: string): Promise<UserBadge[]> {
    return Promise.resolve([...mockUserBadges])
  }

  async getUnlockedBadges(userId: string): Promise<Badge[]> {
    const userBadges = await this.getUserBadges(userId)
    const badgeIds = userBadges.map(ub => ub.badgeId)
    const badges = mockBadges.filter(badge => badgeIds.includes(badge.id))
    return Promise.resolve(badges)
  }

  async getAvailableBadges(userId: string): Promise<Badge[]> {
    const userBadges = await this.getUserBadges(userId)
    const unlockedBadgeIds = new Set(userBadges.map(ub => ub.badgeId))
    const availableBadges = mockBadges.filter(badge => !unlockedBadgeIds.has(badge.id))
    return Promise.resolve(availableBadges)
  }

  async getUserProgress(userId: string): Promise<UserProgress | null> {
    return Promise.resolve({ ...mockUserProgress })
  }

  async updateUserProgress(userId: string, updates: Partial<UserProgress>): Promise<void> {
    Object.assign(mockUserProgress, updates, { updatedAt: new Date().toISOString() })
    return Promise.resolve()
  }

  async addXP(userId: string, xpAmount: number): Promise<void> {
    mockUserProgress.xp += xpAmount
    mockUserProgress.level = Math.floor(mockUserProgress.xp / 1000) + 1
    mockUserProgress.updatedAt = new Date().toISOString()
    return Promise.resolve()
  }

  async trackStudySession(userId: string, duration: number): Promise<void> {
    mockUserProgress.totalStudyTime += duration
    mockUserProgress.lastActivityDate = new Date().toISOString()
    
    const xpGained = Math.floor(duration / 60) * 10
    await this.addXP(userId, xpGained)
    return Promise.resolve()
  }

  async trackExamCompletion(userId: string, score: number): Promise<void> {
    mockUserProgress.totalExamsCompleted += 1
    
    const xpGained = Math.floor(score * 10)
    await this.addXP(userId, xpGained)
    return Promise.resolve()
  }

  async checkBadgeEligibility(userId: string): Promise<string[]> {
    const availableBadges = await this.getAvailableBadges(userId)
    const userProgress = await this.getUserProgress(userId)
    
    if (!userProgress) return []
    
    const newlyUnlockedBadges: string[] = []
    
    for (const badge of availableBadges) {
      let canUnlock = true
      
      for (const condition of badge.conditions) {
        let currentValue = 0
        
        switch (condition.type) {
          case 'study_time':
            currentValue = userProgress.totalStudyTime
            break
          case 'exams_completed':
            currentValue = userProgress.totalExamsCompleted
            break
          case 'current_streak':
            currentValue = userProgress.currentStreak
            break
          case 'longest_streak':
            currentValue = userProgress.longestStreak
            break
          case 'level':
            currentValue = userProgress.level
            break
          case 'xp':
            currentValue = userProgress.xp
            break
          default:
            currentValue = 0
        }
        
        if (currentValue < condition.target) {
          canUnlock = false
          break
        }
      }
      
      if (canUnlock) {
        newlyUnlockedBadges.push(badge.id)
        // Add to user badges
        mockUserBadges.push({
          id: `user-badge-${Date.now()}`,
          userId,
          badgeId: badge.id,
          unlockedAt: new Date().toISOString()
        })
      }
    }
    
    return newlyUnlockedBadges
  }

  // Placeholder methods for compatibility
  async unlockBadge(userId: string, badgeId: string): Promise<UserBadge> {
    const userBadge = {
      id: `user-badge-${Date.now()}`,
      userId,
      badgeId,
      unlockedAt: new Date().toISOString()
    }
    mockUserBadges.push(userBadge)
    return Promise.resolve(userBadge)
  }

  async getBadgeProgress(userId: string): Promise<BadgeProgress[]> {
    return Promise.resolve([])
  }

  async updateBadgeProgress(userId: string, badgeId: string, currentProgress: number, maxProgress: number): Promise<void> {
    return Promise.resolve()
  }

  async createUserProgress(userId: string): Promise<UserProgress> {
    return Promise.resolve({ ...mockUserProgress, userId })
  }

  async getAllAchievements(): Promise<Achievement[]> {
    return Promise.resolve([])
  }

  async getLeaderboard(limitCount: number = 50): Promise<LeaderboardEntry[]> {
    return Promise.resolve([])
  }

  async updateLeaderboard(userId: string, userData: Partial<LeaderboardEntry>): Promise<void> {
    return Promise.resolve()
  }
}

// Use mock service for development
export const gamificationService = new MockGamificationService()
export default gamificationService